import "./AdminUsersPage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { UserApi } from "../../services/api/user";
import { SearchUsersResponse, UserRole } from "../../types/types";
import { isAdminRole } from "../../utils/roles";

type SortField = "id" | "username" | "fullname" | "mail" | "telegram" | "phone" | "role" | "createdAt" | "updatedAt";

const ROLE_OPTIONS: UserRole[] = ["USER", "CREATOR", "ADMIN"];

const toDisplayValue = (value: string | null | undefined) => value ?? "-";

interface QuotaDraft {
  queryNum: string;
  screenshotNum: string;
}

interface SortableThProps {
  field: SortField;
  label: string;
  currentField: SortField;
  desc: boolean;
  onSort: (field: SortField) => void;
}

const SortableTh = ({ field, label, currentField, desc, onSort }: SortableThProps) => {
  const active = currentField === field;
  return (
    <th
      className={
        active
          ? "admin-users__sortable admin-users__sortable_active"
          : "admin-users__sortable"
      }
      aria-sort={active ? (desc ? "descending" : "ascending") : "none"}
      onClick={() => onSort(field)}
    >
      {label}
      {active ? <span className="admin-users__sortIndicator">{desc ? "↓" : "↑"}</span> : null}
    </th>
  );
};

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const [fullnameFilter, setFullnameFilter] = useState("");
  const [usernameFilter, setUsernameFilter] = useState("");
  const [mailFilter, setMailFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");

  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDesc, setSortDesc] = useState(true);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [users, setUsers] = useState<SearchUsersResponse[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editingQuotaId, setEditingQuotaId] = useState<number | null>(null);
  const [quotaDraft, setQuotaDraft] = useState<QuotaDraft>({ queryNum: "", screenshotNum: "" });
  const [savingQuota, setSavingQuota] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  const canAccess = useMemo(() => isAdminRole(user?.role), [user?.role]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await UserApi.searchUsers({
        fullname: fullnameFilter.trim() || null,
        username: usernameFilter.trim() || null,
        mail: mailFilter.trim() || null,
        phone: phoneFilter.trim() || null,
        pageInfo: {
          page,
          size,
          fieldOrder: sortField,
          isDesc: sortDesc,
        },
      });

      setUsers(response.users);
      setTotalElements(response.totalElements);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : "Не удалось загрузить пользователей. Попробуйте еще раз.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [fullnameFilter, mailFilter, page, phoneFilter, size, sortDesc, sortField, usernameFilter]);

  useEffect(() => {
    if (!canAccess) {
      return;
    }

    void fetchUsers();
  }, [canAccess, fetchUsers]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setPage(0);
      void fetchUsers();
    },
    [fetchUsers]
  );

  const handleReset = useCallback(() => {
    setFullnameFilter("");
    setUsernameFilter("");
    setMailFilter("");
    setPhoneFilter("");
    setSortField("createdAt");
    setSortDesc(true);
    setPage(0);
    setSize(20);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    setPage(0);
    setSortField((currentField) => {
      if (currentField !== field) {
        setSortDesc(false);
        return field;
      }

      setSortDesc((currentDesc) => !currentDesc);
      return currentField;
    });
  }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalElements / size)), [size, totalElements]);

  const handleRoleChange = useCallback(
    async (userId: number, role: UserRole) => {
      setError(null);
      try {
        const updatedUser = await UserApi.updateUserRole(userId, { role });
        setUsers((currentUsers) =>
          currentUsers.map((item) => (item.id === userId ? { ...item, role: updatedUser.role } : item))
        );
      } catch (requestError) {
        const message =
          requestError instanceof Error ? requestError.message : "Не удалось обновить роль пользователя.";
        setError(message);
      }
    },
    []
  );

  const startEditingQuota = useCallback((item: SearchUsersResponse) => {
    setEditingQuotaId(item.id);
    setQuotaDraft({
      queryNum: String(item.queryNum ?? ""),
      screenshotNum: String(item.screenshotNum ?? ""),
    });
    setError(null);
  }, []);

  const cancelEditingQuota = useCallback(() => {
    setEditingQuotaId(null);
    setQuotaDraft({ queryNum: "", screenshotNum: "" });
    setError(null);
  }, []);

  const saveQuota = useCallback(
    async (userId: number) => {
      const queryNum = Number(quotaDraft.queryNum);
      const screenshotNum = Number(quotaDraft.screenshotNum);

      if (!Number.isInteger(queryNum) || queryNum < 0 || !Number.isInteger(screenshotNum) || screenshotNum < 0) {
        setError("Лимиты должны быть целыми числами не меньше 0.");
        return;
      }

      setSavingQuota(true);
      setError(null);
      try {
        const updatedUser = await UserApi.updateUserQuota(userId, { queryNum, screenshotNum });
        setUsers((currentUsers) =>
          currentUsers.map((item) =>
            item.id === userId
              ? { ...item, queryNum: updatedUser.queryNum, screenshotNum: updatedUser.screenshotNum }
              : item
          )
        );
        setEditingQuotaId(null);
      } catch (requestError) {
        const message =
          requestError instanceof Error ? requestError.message : "Не удалось обновить лимиты пользователя.";
        setError(message);
      } finally {
        setSavingQuota(false);
      }
    },
    [quotaDraft]
  );

  const handleDelete = useCallback(
    async (item: SearchUsersResponse) => {
      const label = item.username || `ID ${item.id}`;
      if (!window.confirm(`Удалить пользователя ${label}? Будут удалены его данные, подписка, устройства и сессии. Действие необратимо.`)) {
        return;
      }

      setError(null);
      setDeletingUserId(item.id);
      try {
        await UserApi.deleteUser(item.id);
        setUsers((currentUsers) => currentUsers.filter((currentUser) => currentUser.id !== item.id));
        setTotalElements((currentTotal) => Math.max(0, currentTotal - 1));
      } catch (requestError) {
        const message =
          requestError instanceof Error ? requestError.message : "Не удалось удалить пользователя.";
        setError(message);
      } finally {
        setDeletingUserId(null);
      }
    },
    []
  );

  if (!canAccess) {
    return (
      <>
        <Header />
        <main className="admin-users">
          <section className="admin-users__card">
            <p className="admin-users__empty">Доступ к этому разделу есть только у администратора.</p>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="admin-users">
        <section className="admin-users__hero">
          <div>
            <button className="admin-users__back" type="button" onClick={() => navigate("/app/admin")}>
              ← Назад
            </button>
            <p className="admin-users__eyebrow">Админка</p>
            <h1 className="admin-users__title">Пользователи</h1>
            <p className="admin-users__subtitle">
              Поиск по fullname, username, mail и phone, сортировка по клику на заголовке, смена роли и удаление
              пользователя.
            </p>
          </div>
          <aside className="admin-users__summary">
            <div>
              <strong>Всего:</strong> {totalElements}
            </div>
            <div>
              <strong>Страница:</strong> {page + 1} / {totalPages}
            </div>
            <div>
              <strong>Размер:</strong> {size}
            </div>
          </aside>
        </section>

        <section className="admin-users__card">
          <form className="admin-users__filters" onSubmit={handleSubmit}>
            <label className="admin-users__field">
              Fullname
              <input
                className="admin-users__input"
                value={fullnameFilter}
                onChange={(event) => setFullnameFilter(event.target.value)}
                placeholder="Например: Ivanov"
              />
            </label>
            <label className="admin-users__field">
              Username
              <input
                className="admin-users__input"
                value={usernameFilter}
                onChange={(event) => setUsernameFilter(event.target.value)}
                placeholder="Например: ops-error"
              />
            </label>
            <label className="admin-users__field">
              Mail
              <input
                className="admin-users__input"
                value={mailFilter}
                onChange={(event) => setMailFilter(event.target.value)}
                placeholder="Например: john@yandex.ru"
              />
            </label>
            <label className="admin-users__field">
              Phone
              <input
                className="admin-users__input"
                value={phoneFilter}
                onChange={(event) => setPhoneFilter(event.target.value)}
                placeholder="Например: +79001234567"
              />
            </label>

            <div className="admin-users__actions">
              <button className="admin-users__button admin-users__buttonPrimary" type="submit" disabled={loading}>
                {loading ? "Загрузка..." : "Найти"}
              </button>
              <button className="admin-users__button" type="button" onClick={handleReset} disabled={loading}>
                Сбросить
              </button>
              <button className="admin-users__button" type="button" onClick={() => void fetchUsers()} disabled={loading}>
                Обновить
              </button>
            </div>
          </form>
          <p className="admin-users__hint">
            Сортировка: клик по заголовку колонки (в т.ч. Mail и Phone), повторный клик меняет направление. Роль
            изменяется сразу в таблице.
          </p>
          {error ? <p className="admin-users__error">{error}</p> : null}
        </section>

        <section className="admin-users__card admin-users__card_table">
          <div className="admin-users__tableWrap">
            <table className="admin-users__table">
              <thead>
                <tr>
                  <SortableTh field="id" label="ID" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="username" label="Username" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="fullname" label="Fullname" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="mail" label="Mail" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="telegram" label="Telegram" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="phone" label="Phone" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="role" label="Role" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <th>Subscription</th>
                  <th>Запросы</th>
                  <th>Скрины</th>
                  <th>Лимиты</th>
                  <SortableTh field="createdAt" label="Created" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <SortableTh field="updatedAt" label="Updated" currentField={sortField} desc={sortDesc} onSort={handleSort} />
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={14}>
                      <p className="admin-users__empty">{loading ? "Загрузка..." : "Пользователи не найдены."}</p>
                    </td>
                  </tr>
                ) : (
                  users.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td title={item.username}>{item.username}</td>
                      <td title={toDisplayValue(item.fullname)}>{toDisplayValue(item.fullname)}</td>
                      <td title={toDisplayValue(item.mail)}>{toDisplayValue(item.mail)}</td>
                      <td title={toDisplayValue(item.telegram)}>{toDisplayValue(item.telegram)}</td>
                      <td title={toDisplayValue(item.phone)}>{toDisplayValue(item.phone)}</td>
                      <td>
                        <select
                          className="admin-users__roleSelect"
                          value={item.role}
                          onChange={(event) => void handleRoleChange(item.id, event.target.value as UserRole)}
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td title={toDisplayValue(item.subscriptionName)}>{toDisplayValue(item.subscriptionName)}</td>
                      <td>{item.queryNum ?? "-"}</td>
                      <td>{item.screenshotNum ?? "-"}</td>
                      <td>
                        {editingQuotaId === item.id ? (
                          <div className="admin-users__quotaEditor">
                            <label className="admin-users__quotaField">
                              <input
                                className="admin-users__input admin-users__quotaInput"
                                type="number"
                                min={0}
                                value={quotaDraft.queryNum}
                                placeholder="Запросы"
                                disabled={savingQuota}
                                onChange={(event) =>
                                  setQuotaDraft((current) => ({ ...current, queryNum: event.target.value }))
                                }
                              />
                            </label>
                            <label className="admin-users__quotaField">
                              <input
                                className="admin-users__input admin-users__quotaInput"
                                type="number"
                                min={0}
                                value={quotaDraft.screenshotNum}
                                placeholder="Скрины"
                                disabled={savingQuota}
                                onChange={(event) =>
                                  setQuotaDraft((current) => ({ ...current, screenshotNum: event.target.value }))
                                }
                              />
                            </label>
                            <button
                              className="admin-users__button admin-users__buttonPrimary admin-users__quotaButton"
                              type="button"
                              disabled={savingQuota}
                              onClick={() => void saveQuota(item.id)}
                            >
                              {savingQuota ? "..." : "OK"}
                            </button>
                            <button
                              className="admin-users__button admin-users__quotaButton"
                              type="button"
                              disabled={savingQuota}
                              onClick={cancelEditingQuota}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <button
                            className="admin-users__button admin-users__quotaButton"
                            type="button"
                            onClick={() => startEditingQuota(item)}
                          >
                            Изменить
                          </button>
                        )}
                      </td>
                      <td>{item.createdAt}</td>
                      <td>{item.updatedAt}</td>
                      <td>
                        <button
                          className="admin-users__button admin-users__deleteButton"
                          type="button"
                          disabled={deletingUserId === item.id || item.id === user?.id}
                          title={
                            item.id === user?.id
                              ? "Нельзя удалить собственный аккаунт"
                              : "Удалить пользователя"
                          }
                          onClick={() => void handleDelete(item)}
                        >
                          {deletingUserId === item.id ? "Удаляем..." : "Удалить"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-users__card">
          <div className="admin-users__actions">
            <button
              className="admin-users__button admin-users__pagerButton"
              type="button"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={loading || page <= 0}
            >
              ←
            </button>
            <button
              className="admin-users__button admin-users__pagerButton"
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
              disabled={loading || page >= totalPages - 1}
            >
              →
            </button>
            <label className="admin-users__field admin-users__pagerControl">
              <span className="admin-users__visuallyHidden">Количество пользователей на странице</span>
              <input
                className="admin-users__input"
                type="number"
                min={1}
                max={200}
                value={size}
                aria-label="Количество пользователей на странице"
                onChange={(event) => {
                  const nextSize = Number(event.target.value);
                  if (Number.isFinite(nextSize) && nextSize > 0) {
                    setSize(nextSize);
                    setPage(0);
                  }
                }}
              />
            </label>
          </div>
        </section>
      </main>
    </>
  );
};
